import { Button, Card, Form, Input, Typography } from 'antd';
import { FormInstance } from 'antd/es/form';
import React, { useEffect } from 'react';

const { Title, Text } = Typography;

interface Props {
  form: FormInstance;
  selectedPlan: string | null;
  paywallStatus: any;
  user?: any;
  onSubmit: (billingInfo: any) => Promise<void>;
  onBack: () => void;
  loading: boolean;
}

function BillingInformation({
  form,
  selectedPlan,
  paywallStatus,
  user,
  onSubmit,
  onBack,
  loading,
}: Props): React.ReactElement {
  const { priceOne, priceTwo, commissionPercentage } = paywallStatus;

  // Pre-populate form with client invoice details and fallbacks
  useEffect(() => {
    const client = paywallStatus?.client;
    const invoiceDetails = client?.invoiceDetails;

    // Generate user display name for reference fallback
    const userDisplayName =
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.firstName || '';

    const initialValues = {
      company: invoiceDetails?.company || client?.name || '',
      billing: invoiceDetails?.billing || '',
      email: invoiceDetails?.email || '',
      marking: invoiceDetails?.marking || '',
      reference: invoiceDetails?.reference || userDisplayName,
    };

    form.setFieldsValue(initialValues);
  }, [form, paywallStatus, user]);

  const handleFinish = (values: any) => {
    onSubmit(values);
  };

  const getPlanDetails = () => {
    if (selectedPlan === 'fixed') {
      return {
        title: 'Val: Fast pris',
        price: `${priceOne?.toLocaleString('sv-SE')} kr/månad`,
        description: <>Abonnemanget faktureras kvartalsvis</>,
      };
    }
    return {
      title: 'Mix',
      price: `${priceTwo?.toLocaleString(
        'sv-SE',
      )} kr/månad + ${commissionPercentage}% förmedlingsarvode`,
      description: <>Abonnemanget faktureras kvartalsvis</>,
    };
  };

  const planDetails = getPlanDetails();

  return (
    <div>
      <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
        Faktureringsinformation
      </Title>

      <Card style={{ marginBottom: 24, backgroundColor: '#f6f6f6' }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            {planDetails.title}
          </Title>
          <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
            {planDetails.price}
          </Text>
          <br />
          <Text
            type="secondary"
            style={{
              maxWidth: 460,
              margin: '0 auto',
              display: 'block',
            }}
          >
            {planDetails.description}
          </Text>
        </div>
      </Card>

      <Form layout="vertical" onFinish={handleFinish} form={form}>
        <Form.Item
          name="company"
          label="Företagsnamn"
          rules={[{ required: true, message: 'Företagsnamn är obligatoriskt' }]}
        >
          <Input placeholder="Ange företagsnamn" />
        </Form.Item>

        <Form.Item
          name="billing"
          label="Fakturaadress"
          rules={[{ required: true, message: 'Fakturaadress är obligatorisk' }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Ange fullständig fakturaadress"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label="E-postadress för fakturor"
          rules={[
            { required: true, message: 'E-postadress är obligatorisk' },
            { type: 'email', message: 'Ange en giltig e-postadress' },
          ]}
        >
          <Input placeholder="fakturamail@företag.se" />
        </Form.Item>

        <Form.Item name="reference" label="Referens (valfritt)">
          <Input placeholder="Ange referens om det behövs" />
        </Form.Item>

        <Form.Item name="marking" label="Märkning (valfritt)">
          <Input placeholder="Ange märkning om det behövs" />
        </Form.Item>

        <div className="text-sm text-gray-500 my-6">
          ❗ Abonnemanget förnyas kvartalsvis och kan sägas upp med 30 dagars
          varsel innan ny period börjar.
          <br /> Observera att nya villkor gäller från och med den 1:a juli
          2025. All konversation som initierats innan detta datum omfattas av
          tidigare gällande villkor.
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 32,
          }}
        >
          <Button size="large" onClick={onBack}>
            Tillbaka
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={loading}
          >
            Välj & bekräfta
          </Button>
        </div>
      </Form>

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Text type="secondary">
          Genom att aktivera prenumerationen accepterar du våra{' '}
          <a href="/terms" target="_blank">
            användarvillkor
          </a>
          .
        </Text>
      </div>
    </div>
  );
}

export default BillingInformation;
